import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Flip } from 'gsap/Flip';
import { SplitText } from 'gsap/SplitText';
import Lenis from 'lenis';

import '../css/app.css';

import { lenisManager } from './managers/LenisManager.js';
import { swupManager } from './managers/SwupManager.js';
import { animationManager } from './managers/AnimationManager.js';
import { cartState } from './managers/CartState.js';
import { cartDrawerManager } from './managers/CartDrawerManager.js';
import { cartPageManager } from './managers/CartPageManager.js';
import './managers/FacetsManager.js'; 

gsap.registerPlugin(ScrollTrigger, Flip, SplitText);

window.gsap = gsap;
window.Flip = Flip;
window.SplitText = SplitText;
window.Lenis = Lenis;

function initAnimations() {
  animationManager.initRevealAnimations();
  animationManager.initStaggerAnimations();
  animationManager.initParallax();
  animationManager.initImageReveals();
  animationManager.initIntroAnimations();
  animationManager.initSplitTextAnimations();
}

function handleContentReplaced() {
  
  const reinit = () => {
    requestAnimationFrame(() => {
      
      if (typeof window.shouldAnimate === 'function' && window.shouldAnimate()) {
        animationManager.refresh();
        initAnimations();
        ScrollTrigger.refresh();
      }
      cartDrawerManager.reinit();
      cartPageManager.reinit();
    });
  };

  
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(reinit);
  } else {
    reinit();
  }
}

function handleMenuEvents() {
  document.addEventListener('menu:open', () => {
    lenisManager.stop();
  });

  document.addEventListener('menu:close', () => {
    lenisManager.start();
  });
}

function init() {
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  
  const smoothScrollEnabled = window.themeSettings?.enableSmoothScroll !== false;

  
  if (!prefersReducedMotion && smoothScrollEnabled) {
    lenisManager.init();
  }

  
  const pageTransitionsEnabled = window.themeSettings?.enablePageTransitions !== false;

  
  if (!prefersReducedMotion && pageTransitionsEnabled) {
    swupManager.init();

    
    window.addEventListener('swup:contentReplaced', handleContentReplaced);
  }

  
  const animationsEnabled = window.themeSettings?.enableAnimations !== false;

  
  if (!prefersReducedMotion && animationsEnabled) {
    
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        requestAnimationFrame(() => {
          initAnimations();
          ScrollTrigger.refresh();
        });
      });
    } else {
      
      requestAnimationFrame(() => {
        initAnimations();
      });
    }
  }

  
  handleMenuEvents();

  
  cartState.init();

  
  cartState.subscribe((cart) => {
    if (cart) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        
        if (cart.item_count > 0) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });
    }
  });

  
  cartDrawerManager.init();
  cartPageManager.init();

  
  document.addEventListener('cart:updated', () => {
    swupManager.clearCache('/cart');
  });

  
  document.addEventListener('click', (e) => {
    const anchor = e.target.closest('a[href^="#"]');
    if (anchor && anchor.getAttribute('href').length > 1) {
      e.preventDefault();
      const target = document.querySelector(anchor.getAttribute('href'));
      if (target) {
        lenisManager.scrollTo(target, {
          offset: -100, 
        });
      }
    }
  });

  
  window.pieces = {
    lenis: lenisManager,
    swup: swupManager,
    animation: animationManager,
    cartState: cartState,
    cartDrawer: cartDrawerManager,
    cartPage: cartPageManager,
    gsap,
    ScrollTrigger,
    Flip,
    SplitText,
    Lenis,
  };

  
  window.openCartDrawer = () => cartDrawerManager.open();
  window.closeCartDrawer = () => cartDrawerManager.close();
  window.refreshCartDrawer = () => cartDrawerManager.refresh();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (typeof window.shouldAnimate === 'function' && window.shouldAnimate()) {
      animationManager.refresh();
      initAnimations();
    }
  });

  document.addEventListener('shopify:section:unload', () => {
    animationManager.destroy();
  });

  document.addEventListener('shopify:section:reorder', () => {
    if (typeof window.shouldAnimate === 'function' && window.shouldAnimate()) {
      animationManager.refresh();
    }
  });
}
